import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Policy } from "@/hooks/usePolicies";
import { PresetGeneralTab } from "./PresetGeneralTab";
import { PresetCategoriesTab } from "./PresetCategoriesTab";
import { PresetWhitelistTab } from "./PresetWhitelistTab";
import { PresetBlacklistTab } from "./PresetBlacklistTab";
import { PresetScopeTab } from "./PresetScopeTab";

interface PresetDetailViewProps {
  preset: Policy;
  onBack: () => void;
}

export const PresetDetailView = ({ preset, onBack }: PresetDetailViewProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'general';

  const handleTabChange = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', value);
    setSearchParams(newSearchParams);
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'draft':
        return 'secondary';
      case 'inactive':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="flex items-center space-x-2"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Policies</span>
        </Button>
      </div>

      {/* Preset Info Card */}
      <Card>
        <CardContent>
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Settings className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{preset.name}</h1>
                {preset.description && (
                  <p className="text-gray-600 mt-1">{preset.description}</p>
                )}
                <div className="flex items-center space-x-2 mt-2">
                  <Badge variant="default">
                    Active
                  </Badge>
                  <Badge variant="outline">
                    Policy
                  </Badge>
                </div>
              </div>
            </div>
            <Button variant="outline">
              Edit Policy
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Policy Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="whitelist">Whitelist</TabsTrigger>
          <TabsTrigger value="blacklist">Blacklist</TabsTrigger>
          <TabsTrigger value="scope">Scope</TabsTrigger>
        </TabsList>
        
        <TabsContent value="general" className="mt-6">
          <PresetGeneralTab preset={preset} />
        </TabsContent>
        
        <TabsContent value="categories" className="mt-6">
          <PresetCategoriesTab preset={preset} />
        </TabsContent>
        
        <TabsContent value="whitelist" className="mt-6">
          <PresetWhitelistTab preset={preset} />
        </TabsContent>
        
        <TabsContent value="blacklist" className="mt-6">
          <PresetBlacklistTab preset={preset} />
        </TabsContent>
        
        <TabsContent value="scope" className="mt-6">
          <PresetScopeTab preset={preset} />
        </TabsContent>
      </Tabs>
    </div>
  );
};